import { useEffect, useRef, useState } from "react";
import { MessageCircle, Mic, Square, Send, Volume2, X, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";

const LANGS = [["auto", "Auto"], ["en", "English"], ["hi", "हिंदी"]];
const MAX_MS = 30000;
const STATUS = {
  asking: "Allow microphone access in the browser prompt…",
  denied: "Microphone is blocked. Allow it in the browser's site settings, or type your message below.",
  unsupported: "Voice isn't supported on this browser. Please type your message below.",
  recording: "Listening… tap stop when you're done (max 30 seconds).",
  processing: "Processing your voice…",
  thinking: "Receptionist is replying…",
};

function useRecorder(onAudio, setState) {
  const rec = useRef(null), timer = useRef(null), cancelled = useRef(false);
  const stopTracks = (s) => s?.getTracks().forEach((t) => t.stop());
  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === "undefined") { setState("unsupported"); return; }
    setState("asking");
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch { setState("denied"); return; }
    const chunks = [];
    const r = new MediaRecorder(stream);
    r.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    r.onstop = () => { clearTimeout(timer.current); stopTracks(stream); if (!cancelled.current) onAudio(new Blob(chunks, { type: r.mimeType || "audio/webm" })); };
    cancelled.current = false;
    rec.current = r;
    r.start();
    setState("recording");
    timer.current = setTimeout(() => { if (r.state === "recording") r.stop(); }, MAX_MS);
  };
  const stop = () => { if (rec.current?.state === "recording") rec.current.stop(); };
  const cancel = () => { cancelled.current = true; stop(); };
  useEffect(() => () => { clearTimeout(timer.current); cancel(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return { start, stop, cancel };
}

function Bubble({ m, onReplay, busy }) {
  const me = m.from === "me";
  return (
    <div className={`flex ${me ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${me ? "bg-[var(--sb-plum)] text-white" : "bg-slate-100 text-slate-800"}`} data-testid={`rcp-msg-${m.i}`}>
        {m.voice && <Mic className="mr-1 inline h-3 w-3 opacity-70" />}{m.text}
        {!me && <button type="button" disabled={busy} onClick={() => onReplay(m.text)} className="ml-2 inline-flex align-middle opacity-60 hover:opacity-100" aria-label="Play reply" data-testid={`rcp-replay-${m.i}`}><Volume2 className="h-3.5 w-3.5" /></button>}
      </div>
    </div>
  );
}

export function ReceptionistPanel() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [opts, setOpts] = useState([]);
  const [text, setText] = useState("");
  const [lang, setLang] = useState("auto");
  const [speakOn, setSpeakOn] = useState(true);
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const sid = useRef(""), audio = useRef(null), end = useRef(null), lastAudio = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs, state]);

  const add = (m) => setMsgs((p) => [...p, { ...m, i: p.length }]);
  const play = async (t) => {
    try {
      const r = await api.post("/receptionist/speak", { text: t }, { responseType: "blob" });
      audio.current?.pause();
      audio.current = new Audio(URL.createObjectURL(r.data));
      await audio.current.play();
    } catch { setErr("Spoken reply unavailable. You can still read the reply."); }
  };
  const send = async (msg, voice = false) => {
    const m = (msg || "").trim();
    if (!m) return;
    setErr(""); setText(""); setOpts([]);
    add({ from: "me", text: m, voice });
    setState("thinking");
    try {
      const d = (await api.post("/receptionist/turn", { session_id: sid.current, message: m, language: lang })).data;
      sid.current = d.session_id;
      add({ from: "bot", text: d.reply, stage: d.stage });
      setOpts(d.options || []);
      setState("idle");
      if (speakOn) play(d.reply);
    } catch (e) { setState("idle"); setErr(`${apiErr(e)} — please try again.`); }
  };
  const onAudio = async (blob) => {
    lastAudio.current = blob;
    setState("processing");
    try {
      const fd = new FormData();
      fd.append("file", blob, `speech.${(blob.type.split("/")[1] || "webm").split(";")[0]}`);
      fd.append("language", lang);
      const d = (await api.post("/receptionist/transcribe", fd)).data;
      lastAudio.current = null;
      await send(d.text, true);
    } catch (e) { setState("idle"); setErr(`${apiErr(e)}`); }
  };
  const { start, stop, cancel } = useRecorder(onAudio, (s) => { setErr(""); setState(s); });
  const reset = () => { cancel(); lastAudio.current = null; sid.current = ""; setMsgs([]); setOpts([]); setErr(""); setState("idle"); audio.current?.pause(); };
  const busy = ["asking", "recording", "processing", "thinking"].includes(state);

  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} className="fixed bottom-5 right-5 z-30 flex items-center gap-2 rounded-full bg-[var(--sb-plum)] px-5 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-105" data-testid="rcp-open">
      <MessageCircle className="h-4 w-4" /> Ask our receptionist
    </button>
  );
  return (
    <div className="fixed bottom-4 right-4 z-30 flex h-[70vh] max-h-[620px] w-[calc(100vw-2rem)] max-w-sm flex-col rounded-3xl bg-white shadow-2xl ring-1 ring-black/5" data-testid="rcp-panel">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <span className="font-semibold text-[var(--sb-plum)]">Receptionist</span>
        <span className="flex items-center gap-2">
          <select value={lang} onChange={(e) => setLang(e.target.value)} className="rounded-md border px-1.5 py-1 text-xs" data-testid="rcp-lang">
            {LANGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <button type="button" onClick={() => setSpeakOn(!speakOn)} className={`rounded-md border px-1.5 py-1 text-xs ${speakOn ? "text-[var(--sb-plum)]" : "text-slate-600 line-through"}`} data-testid="rcp-voice-toggle">Voice</button>
          <button type="button" onClick={reset} className="text-xs text-slate-500 underline" data-testid="rcp-reset">New</button>
          <button type="button" onClick={() => { cancel(); audio.current?.pause(); setState("idle"); setOpen(false); }} aria-label="Close" className="-m-2 flex h-10 w-10 items-center justify-center" data-testid="rcp-close"><X className="h-5 w-5" /></button>
        </span>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto px-4 py-3" data-testid="rcp-messages">
        {!msgs.length && <p className="text-sm text-slate-500">Ask about services, prices or opening hours, or request an appointment, in English or Hindi. Tap the mic to speak.</p>}
        {msgs.map((m) => <Bubble key={m.i} m={m} onReplay={play} busy={busy} />)}
        {STATUS[state] && <p className="flex items-center gap-1.5 text-xs text-slate-500" data-testid={`rcp-state-${state}`}>{["processing", "thinking", "asking"].includes(state) && <Loader2 className="h-3 w-3 animate-spin" />}{STATUS[state]}</p>}
        {err && <p className="rounded-md bg-rose-50 px-2 py-1.5 text-xs text-rose-700" data-testid="rcp-error">{err}{lastAudio.current && <button type="button" onClick={() => onAudio(lastAudio.current)} className="ml-2 font-semibold underline" data-testid="rcp-retry">Retry</button>}</p>}
        <div ref={end} />
      </div>
      {opts.length > 0 && <div className="flex flex-wrap gap-1.5 px-4 pb-2" data-testid="rcp-options">{opts.map((o) => <button key={o} type="button" disabled={busy} onClick={() => send(o)} className="rounded-full border px-2.5 py-1 text-xs hover:bg-slate-50" data-testid={`rcp-opt-${o}`}>{o}</button>)}</div>}
      <form onSubmit={(e) => { e.preventDefault(); send(text); }} className="flex items-center gap-2 border-t px-3 py-3">
        {state === "recording"
          ? <button type="button" onClick={stop} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-600 text-white animate-pulse" aria-label="Stop recording" data-testid="rcp-mic-stop"><Square className="h-4 w-4" /></button>
          : <button type="button" disabled={busy} onClick={start} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--sb-plum)] text-white disabled:opacity-40" aria-label="Speak" data-testid="rcp-mic"><Mic className="h-4 w-4" /></button>}
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={500} disabled={state === "recording"} placeholder="Type a message…" className="min-w-0 flex-1 rounded-full border px-3 py-2 text-sm" data-testid="rcp-input" />
        <button type="submit" disabled={busy || !text.trim()} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border disabled:opacity-40" aria-label="Send" data-testid="rcp-send"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
}
